import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomFieldsForRole } from '@/hooks/useCustomFieldsForRole';
import { FieldsetCustomFieldDefinition } from '@payaca/custom-fields';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { useMemo } from 'react';
import { UNASSIGNED, encodeTaskAssignee } from '@/helpers/taskAssignee';

type UserCustomFieldOption = {
  /** Dot-path identifier stored on the template (e.g. `projectAdmin.projectManager`). */
  identifier: string;
  /** The leaf field's own label (e.g. `Project Manager`). */
  leafLabel: string;
  /** The full breadcrumb through any fieldsets (e.g. `Project Admin › Project Manager`). */
  fullPath: string;
};

const PATH_SEPARATOR = ' › ';

/**
 * Enumerate the project's user-type custom fields, resolving each to its leaf
 * label and full fieldset breadcrumb.
 */
const useUserCustomFieldOptions = (): UserCustomFieldOption[] => {
  const { customFields: projectCustomFields } =
    useCustomFieldsForRole('project');

  return useMemo(() => {
    const options: UserCustomFieldOption[] = [];

    for (const field of projectCustomFields) {
      if (field.type === 'user') {
        const label = field.schema.metadata['payaca:label'] ?? field.identifier;
        options.push({
          identifier: field.identifier,
          leafLabel: label,
          fullPath: label,
        });
      } else if (field.type === 'fieldset') {
        const fieldsetDefinition = new FieldsetCustomFieldDefinition(
          field.schema
        );
        const allChildren = fieldsetDefinition.getChildrenWithPaths();

        const labelByPath = new Map<string, string>();
        for (const { definition, path } of allChildren) {
          labelByPath.set(path.join('.'), definition.label);
        }

        allChildren
          .filter(({ definition }) => definition.type === 'user')
          .forEach(({ path }) => {
            const segmentLabels = path.slice(1).map((_, i) => {
              const progressivePath = path.slice(0, i + 2).join('.');
              return labelByPath.get(progressivePath) ?? path[i + 1];
            });
            options.push({
              identifier: path.join('.'),
              leafLabel:
                segmentLabels[segmentLabels.length - 1] ?? path.join('.'),
              fullPath: [fieldsetDefinition.label, ...segmentLabels].join(
                PATH_SEPARATOR
              ),
            });
          });
      }
    }

    return options;
  }, [projectCustomFields]);
};

/**
 * Look up a user-custom-field assignee's display labels by its stored
 * identifier. Used to render an existing assignee concisely (leaf label, with
 * the full breadcrumb available on hover).
 */
export const useUserCustomFieldLabelByIdentifier = () => {
  const options = useUserCustomFieldOptions();

  return useMemo(
    () =>
      new Map(
        options.map(({ identifier, leafLabel, fullPath }) => [
          identifier,
          { leafLabel, fullPath },
        ])
      ),
    [options]
  );
};

const CUSTOM_FIELDS_GROUP = 'customFields';
const TEAMS_GROUP = 'teams';
const USERS_GROUP = 'users';

type AssigneeOption = { label: string; value: string; groupId: string };

/**
 * The groups an assignee select can show, in the order they render.
 *
 * Every option must belong to one of them. A `Select` renders grouped options
 * ahead of ungrouped ones, so an ungrouped option lands below the final heading
 * and reads as part of that section.
 */
const GROUPS = [
  { id: CUSTOM_FIELDS_GROUP, label: 'Custom fields' },
  { id: TEAMS_GROUP, label: 'Teams' },
  { id: USERS_GROUP, label: 'Users' },
];

const groupsPresentIn = (options: AssigneeOption[]) =>
  GROUPS.filter((group) => options.some((o) => o.groupId === group.id));

const UNASSIGNED_OPTION: AssigneeOption = {
  label: 'Unassigned',
  value: UNASSIGNED,
  groupId: USERS_GROUP,
};

const useUserOptions = (): AssigneeOption[] => {
  const { data: accountUsers } = useGetMyAccountUsersExcludedDeactivated();

  return useMemo(
    () =>
      accountUsers.map((user) => ({
        label: user.fullName,
        value: encodeTaskAssignee({
          type: 'user',
          userPublicId: user.publicId,
        }),
        groupId: USERS_GROUP,
      })),
    [accountUsers]
  );
};

const useTeamOptions = (): AssigneeOption[] => {
  const { userHasRequiredPermission } = usePermissions();
  // The resolver refuses a caller without the permission, so asking anyway
  // costs a rejected request and a Sentry event per retry, every mount.
  const { data: accountTeams } = useGetMyAccountTeams({
    enabled: userHasRequiredPermission([AccountsPermissions.GET_TEAMS]),
  });

  return useMemo(
    () =>
      (accountTeams ?? []).map((team) => ({
        label: team.name,
        value: encodeTaskAssignee({ type: 'team', teamPublicId: team.id }),
        groupId: TEAMS_GROUP,
      })),
    [accountTeams]
  );
};

/**
 * Options and groups for a task's assignee select: the account's Teams, then
 * unassigned and its active users.
 */
export const useTaskAssigneeOptions = () => {
  const userOptions = useUserOptions();
  const teamOptions = useTeamOptions();

  return useMemo(() => {
    const options = [UNASSIGNED_OPTION, ...teamOptions, ...userOptions];

    return { options, optionGroups: groupsPresentIn(options) };
  }, [teamOptions, userOptions]);
};

/**
 * Options and groups for a task template's assignee select: everything a task
 * can be assigned to, plus the assignees resolved per project when the task is
 * created — the Project owner, and the project's user-type custom fields (incl.
 * nested fieldset paths) collected under "Custom fields" so each option needs
 * no repeated prefix.
 */
export const useTemplateAssigneeOptions = () => {
  const userOptions = useUserOptions();
  const teamOptions = useTeamOptions();
  const userCustomFieldOptions = useUserCustomFieldOptions();

  return useMemo(() => {
    const options: AssigneeOption[] = [
      UNASSIGNED_OPTION,
      {
        label: 'Project owner',
        value: 'projectOwner',
        groupId: USERS_GROUP,
      },
      ...userCustomFieldOptions.map(({ identifier, fullPath }) => ({
        label: fullPath,
        value: `userCustomField:${identifier}`,
        groupId: CUSTOM_FIELDS_GROUP,
      })),
      ...teamOptions,
      ...userOptions,
    ];

    return { options, optionGroups: groupsPresentIn(options) };
  }, [teamOptions, userOptions, userCustomFieldOptions]);
};
